import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { ListScreen, Notice, TableSkeleton } from '../components/chrome/list-screen'
import { Screen, ScreenHeader } from '../components/chrome/shell'
import { Button } from '../components/ui/button'
import { Blank, Panel } from '../components/ui/panel'
import { Table, TBody, Td, Th, THead, Tr } from '../components/ui/table'
import { Chip, type Tone } from '../components/ui/tone'
import { api, type Page, type Reply, type ReplyClass, type ReplyClassCount } from '../lib/api'
import { useSession } from '../lib/session'

/**
 * Replies (§36, §37, §59).
 *
 * Guarded by `view_raw_replies` rather than `view_contacts`, and §59 is the reason: a reply body is
 * somebody's correspondence. ANALYST deliberately cannot read this screen — analysis work needs the
 * classification, not the prose.
 *
 * The list shows the classifier's verdict **and** its confidence, and marks the rows a person
 * overrode. Both verdicts survive an override (§36), which is the only way to find out later that
 * the classifier is wrong about a particular kind of message.
 *
 * §37 has its own column here: out-of-office is not a positive reply and is not a negative one, and
 * a screen that folds it into either teaches an operator to chase people who are simply away.
 */

const CLASS_LABEL: Record<ReplyClass, string> = {
  POSITIVE: 'Interested',
  NEGATIVE: 'Not interested',
  NEUTRAL: 'Neutral',
  OUT_OF_OFFICE: 'Out of office',
  AUTO_REPLY: 'Auto-reply',
  BOUNCE: 'Bounce',
  UNSUBSCRIBE_REQUEST: 'Unsubscribe request',
  COMPLAINT: 'Complaint',
  UNCLASSIFIED: 'Unclassified',
}

const CLASS_TONE: Record<ReplyClass, Tone> = {
  POSITIVE: 'ok',
  NEGATIVE: 'neutral',
  NEUTRAL: 'neutral',
  OUT_OF_OFFICE: 'neutral',
  AUTO_REPLY: 'neutral',
  BOUNCE: 'warn',
  UNSUBSCRIBE_REQUEST: 'warn',
  COMPLAINT: 'bad',
  UNCLASSIFIED: 'neutral',
}

export function RepliesScreen() {
  const { can } = useSession()
  const [search, setSearch] = useState('')
  const [replyClass, setReplyClass] = useState<string | undefined>(undefined)

  const replies = useQuery({
    queryKey: ['replies', replyClass],
    queryFn: () =>
      api.get<Page<Reply>>(`/replies?take=200${replyClass ? `&replyClass=${replyClass}` : ''}`),
    enabled: can('view_raw_replies'),
  })

  const classes = useQuery({
    queryKey: ['replies', 'classes'],
    queryFn: () => api.get<ReplyClassCount[]>('/replies/classes'),
    enabled: can('view_raw_replies'),
  })

  if (!can('view_raw_replies')) {
    return (
      <Screen>
        <ScreenHeader title="Replies" description="What people wrote back." />
        <Panel>
          <Blank
            icon="denied"
            title="You do not have access to replies"
            body="Reading them requires the view_raw_replies permission, which is deliberately narrow: a reply is someone's personal correspondence (§59)."
          />
        </Panel>
      </Screen>
    )
  }

  const rows = (replies.data?.items ?? []).filter((reply) => {
    if (!search) return true
    const needle = search.toLowerCase()
    return (
      reply.fromEmail.toLowerCase().includes(needle) ||
      (reply.subject ?? '').toLowerCase().includes(needle)
    )
  })

  const tabs = [
    { id: 'all', label: 'Everything', count: replies.data?.total },
    ...(classes.data ?? [])
      .filter((row) => row.replyClass !== null)
      .map((row) => ({
        id: row.replyClass as string,
        label: CLASS_LABEL[row.replyClass as ReplyClass],
        count: row.count,
      })),
  ]

  return (
    <ListScreen
      title="Replies"
      subtitle="What people wrote back, and what the classifier made of it. A person can disagree with the classifier; both verdicts are kept."
      query={search}
      onQuery={setSearch}
      queryPlaceholder="Filter by sender or subject"
      tabs={tabs}
      activeTab={replyClass ?? 'all'}
      onTab={(id) => setReplyClass(id === 'all' ? undefined : id)}
      notice={
        <Notice tone="info">
          Nothing is collecting replies yet. Inbound sync — Gmail Pub/Sub, Microsoft Graph
          notifications and IMAP — is Phase 4, and reply detection on Google additionally needs a
          restricted scope with an annual audit behind it (P028). Until then this list is genuinely
          empty rather than waiting.
        </Notice>
      }
    >
      {replies.isLoading ? (
        <TableSkeleton columns={5} />
      ) : replies.isError ? (
        <Blank
          icon="error"
          tone="bad"
          title="Replies could not be loaded"
          action={<Button onClick={() => void replies.refetch()}>Try again</Button>}
        />
      ) : rows.length > 0 ? (
        <Table>
          <THead>
            <Th width={150}>Received</Th>
            <Th width={230}>From</Th>
            <Th>Subject</Th>
            <Th width={170}>Classified as</Th>
            <Th width={120}>Confidence</Th>
          </THead>
          <TBody>
            {rows.map((reply) => (
              <Tr key={reply.id}>
                <Td className="text-muted">{new Date(reply.receivedAt).toLocaleString()}</Td>
                <Td mono>{reply.fromEmail}</Td>
                <Td className="text-muted">
                  {reply.subject ?? <span className="text-faint">no subject</span>}
                </Td>
                <Td>
                  {reply.replyClass ? (
                    <span className="flex items-center gap-1.5">
                      <Chip tone={CLASS_TONE[reply.replyClass]}>
                        {CLASS_LABEL[reply.replyClass]}
                      </Chip>
                      {/* An overridden row has to look different, or the disagreement between a
                          person and the classifier is invisible in aggregate. */}
                      {reply.overriddenById && <span className="text-faint text-[12px]">by hand</span>}
                    </span>
                  ) : (
                    <span className="text-faint">not classified</span>
                  )}
                </Td>
                <Td className="text-muted tabular-nums">
                  {reply.classifierConfidence === null
                    ? '—'
                    : `${Math.round(reply.classifierConfidence * 100)}%`}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      ) : search || replyClass ? (
        <Blank
          icon="noResults"
          title="Nothing matches that filter."
          action={
            <Button
              onClick={() => {
                setSearch('')
                setReplyClass(undefined)
              }}
            >
              Clear filter
            </Button>
          }
        />
      ) : (
        <Blank
          icon="replies"
          title="No replies yet."
          body="Nothing has been sent, so nothing has come back. When inbound sync lands, replies appear here classified, and stop-on-reply halts the sequence for that contact automatically."
        />
      )}
    </ListScreen>
  )
}
